import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { cn } from "@/lib/utils";
import { 
  Plus, Pencil, Trash2, ChevronLeft, 
  HelpCircle, Lightbulb, Check,
  ChevronRight, Save, LayoutList, Trophy
} from "lucide-react";
import { toast } from "sonner";
import { ConfirmDeleteDialog } from "@/components/common/ConfirmDeleteDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  useGetTestsQuery,
  useGetTestQuestionsQuery, 
  useAddQuestionMutation, 
  useUpdateQuestionMutation, 
  useDeleteQuestionMutation,
  Question 
} from "@/redux/slices/testApi";

const questionSchema = z.object({
  text: z.string().min(5, "Question text too short"),
  image: z.string().optional().default(""),
  type: z.enum(["mcq", "multiple", "input"]).default("mcq"),
  options: z.array(z.string().optional()).optional(),
  correctAnswer: z.any(),
  marks: z.coerce.number().min(1),
  explanation: z.string().optional().default(""),
});
type QuestionFormValues = z.infer<typeof questionSchema>;

export default function QuestionManagementPage() {
  const { testId } = useParams();
  const navigate = useNavigate();
  
  const { data: testsData } = useGetTestsQuery({});
  const test = testsData?.data?.find(t => t.id === testId);
  
  const { data: questionsData, isFetching } = useGetTestQuestionsQuery(testId!);
  const [addQuestion, addM] = useAddQuestionMutation();
  const [updateQuestion, updateM] = useUpdateQuestionMutation();
  const [deleteQuestion, deleteM] = useDeleteQuestionMutation();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Question | null>(null);
  const [toDelete, setToDelete] = useState<Question | null>(null);

  const form = useForm<QuestionFormValues>({
    resolver: zodResolver(questionSchema),
    defaultValues: {
      text: "", image: "", options: ["", "", "", ""], 
      correctAnswer: 0, marks: 1, explanation: ""
    }
  });

  const openCreate = () => {
    setEditing(null);
    form.reset({ 
      text: "", image: "", type: "mcq", options: ["", "", "", ""], 
      correctAnswer: 0, marks: 1, explanation: "" 
    });
    setOpen(true);
  };

  const openEdit = (q: Question) => {
    setEditing(q);
    
    let parsedOptions = ["", "", "", ""];
    if (Array.isArray(q.options)) {
      parsedOptions = q.options;
    } else if (typeof q.options === 'string') {
      try { parsedOptions = JSON.parse(q.options); } catch {}
    }
    
    let parsedCorrectAnswer = q.correctAnswer;
    if (q.type === 'multiple' && typeof q.correctAnswer === 'string') {
      try { parsedCorrectAnswer = JSON.parse(q.correctAnswer); } catch {}
    }

    form.reset({
      text: q.text,
      image: q.image || "",
      type: q.type || "mcq",
      options: parsedOptions,
      correctAnswer: parsedCorrectAnswer,
      marks: q.marks || 1,
      explanation: q.explanation || ""
    } as any);
    setOpen(true);
  };

  const onSubmit = async (v: QuestionFormValues) => {
    // Validate options and correct answers manually based on type
    if (v.type === "mcq") {
      if (!v.options || v.options.length < 4 || v.options.some(o => !o || o.trim() === "")) {
        toast.error("Please fill in all 4 options for a single-choice question");
        return;
      }
      if (typeof v.correctAnswer !== "number" && isNaN(Number(v.correctAnswer))) {
        toast.error("Please select a valid correct option");
        return;
      }
    } else if (v.type === "multiple") {
      if (!v.options || v.options.length < 4 || v.options.some(o => !o || o.trim() === "")) {
        toast.error("Please fill in all 4 options for a multi-select question");
        return;
      }
      if (!Array.isArray(v.correctAnswer) || v.correctAnswer.length === 0) {
        toast.error("Please select at least one correct option for a multi-select question");
        return;
      }
    } else if (v.type === "input") {
      if (typeof v.correctAnswer !== "string" || v.correctAnswer.trim() === "") {
        toast.error("Please enter the correct answer text");
        return;
      }
    }

    try {
      if (editing) {
        await updateQuestion({ id: editing.id, body: v }).unwrap();
        toast.success("Question updated successfully");
      } else {
        await addQuestion({ testId: testId!, body: v }).unwrap();
        toast.success("New question added to assessment");
      }
      setOpen(false);
    } catch {
      toast.error("Failed to save question data");
    }
  };

  if (!test && !isFetching) return <div className="p-8 text-center font-bold text-muted-foreground uppercase tracking-widest italic">Assessment session not found.</div>;

  return (
    <div className="min-h-screen bg-muted/20">
      <div className="bg-card/80 backdrop-blur-md border-b px-6 py-4 flex items-center justify-between sticky top-0 z-20 shadow-sm transition-all">
        <div className="flex items-center gap-4">
           <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-full hover:bg-muted/80">
              <ChevronLeft className="h-5 w-5" />
           </Button>
           <div className="space-y-0.5">
              <h1 className="text-base font-black tracking-tight text-foreground/90">{test?.title}</h1>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-muted-foreground uppercase font-black tracking-widest">Question Management</span>
                <span className="h-1 w-1 rounded-full bg-muted-foreground/30" />
                <span className="text-[10px] text-primary font-black uppercase tracking-widest">{questionsData?.data?.length || 0} Total Items</span>
              </div>
           </div>
        </div>
        <Button onClick={openCreate} className="shadow-xl shadow-primary/20 rounded-xl h-10 px-6 font-bold uppercase tracking-wider text-xs">
           <Plus className="h-4 w-4 mr-1.5" /> Add Question
        </Button>
      </div>

      <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
        {isFetching ? (
           <div className="space-y-4">
              {[1, 2, 3].map(i => <div key={i} className="h-48 animate-pulse rounded-2xl bg-card/60 backdrop-blur-sm border border-border/50" />)}
           </div>
        ) : (
          <div className="space-y-4">
            {questionsData?.data?.map((q, idx) => (
              <Card key={q.id} className="group relative overflow-hidden bg-card/60 backdrop-blur-sm shadow-sm ring-1 ring-border hover:shadow-lg transition-all duration-300">
                 <div className="absolute top-0 left-0 h-full w-1 bg-primary/10 transition-all group-hover:bg-primary" />
                 
                 <CardHeader className="p-6 pb-4 flex flex-row items-start justify-between gap-6">
                    <div className="flex gap-4">
                       <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-muted font-mono text-[11px] font-black text-muted-foreground shadow-inner">
                          {idx + 1}
                       </div>
                       <div className="space-y-2">
                          <p className="text-sm font-bold leading-relaxed text-foreground/80">{q.text}</p>
                          {q.image && (
                            <div className="mt-4 rounded-2xl border bg-muted/20 p-2 overflow-hidden max-w-md">
                              <img src={q.image} className="rounded-xl w-full object-contain max-h-64" />
                            </div>
                          )}
                       </div>
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-all translate-x-2 group-hover:translate-x-0">
                       <Button size="icon" variant="ghost" className="h-9 w-9 rounded-full hover:bg-primary/10 hover:text-primary" onClick={() => openEdit(q)}>
                          <Pencil className="h-4 w-4" />
                       </Button>
                       <Button size="icon" variant="ghost" className="h-9 w-9 rounded-full hover:bg-destructive/10 hover:text-destructive" onClick={() => setToDelete(q)}>
                          <Trash2 className="h-4 w-4" />
                       </Button>
                    </div>
                 </CardHeader>

                 <CardContent className="px-6 pb-6 pl-16">
                     <div className="mb-4 flex gap-2">
                       <span className="text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-lg bg-primary/10 text-primary">
                         {q.type ? q.type.toUpperCase() : "MCQ"}
                       </span>
                     </div>

                     {q.type === "input" ? (
                       <div className="flex items-center gap-3 p-3.5 px-4 rounded-xl border border-green-500/20 bg-green-500/5 text-green-700 font-bold text-[11px] max-w-md">
                          <Check className="h-4 w-4 text-green-500 shrink-0" />
                          <span className="flex-1">Correct Answer: {String(q.correctAnswer)}</span>
                       </div>
                      ) : (
                       <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {(() => {
                             let parsedOpts: string[] = [];
                             if (Array.isArray(q.options)) {
                               parsedOpts = q.options;
                             } else if (typeof q.options === 'string') {
                               try { parsedOpts = JSON.parse(q.options); } catch {}
                             }
                             
                             return parsedOpts.map((opt, i) => {
                               let isCorrect = false;
                               if (q.type === "multiple") {
                                 try {
                                   if (Array.isArray(q.correctAnswer)) {
                                     isCorrect = q.correctAnswer.includes(i);
                                   } else if (typeof q.correctAnswer === 'string') {
                                     isCorrect = JSON.parse(q.correctAnswer).includes(i);
                                   }
                                 } catch (e) {
                                   isCorrect = false;
                                 }
                               } else {
                                 isCorrect = i === Number(q.correctAnswer);
                               }

                               return (
                                  <div key={i} className={cn(
                                     "flex items-center gap-3 p-3 px-4 rounded-xl border text-[11px] transition-all duration-300",
                                     isCorrect 
                                       ? "bg-green-500/5 border-green-500/20 text-green-700 font-bold shadow-sm" 
                                       : "bg-muted/30 border-transparent text-muted-foreground/80"
                                  )}>
                                     <div className={cn(
                                        "flex h-5 w-5 shrink-0 items-center justify-center rounded-lg text-[9px] font-black transition-colors",
                                        isCorrect ? "bg-green-500 text-white" : "bg-black/5 text-muted-foreground"
                                     )}>
                                        {String.fromCharCode(65 + i)}
                                     </div>
                                     <span className="flex-1">{opt}</span>
                                     {isCorrect && <Check className="h-3.5 w-3.5 text-green-500" />}
                                  </div>
                               );
                             });
                          })()}
                       </div>
                     )}
                     {q.explanation && (
                        <div className="mt-5 p-4 rounded-2xl bg-amber-500/5 border border-amber-500/10 flex gap-4 shadow-inner">
                           <Lightbulb className="h-5 w-5 text-amber-500 shrink-0" />
                           <div className="space-y-1">
                             <span className="text-[10px] font-black uppercase text-amber-600/60 tracking-widest">Answer Rationale</span>
                             <p className="text-[11px] text-amber-900/80 leading-relaxed italic">{q.explanation}</p>
                           </div>
                        </div>
                     )}
                  </CardContent>
              </Card>
            ))}
          </div>
        )}

        {!isFetching && questionsData?.data?.length === 0 && (
           <div className="flex flex-col items-center justify-center py-32 bg-card/50 backdrop-blur-sm rounded-[2.5rem] border-2 border-dashed border-primary/10 shadow-inner">
              <div className="p-6 rounded-full bg-primary/5 mb-6 ring-1 ring-primary/10">
                <HelpCircle className="h-12 w-12 text-primary/30" />
              </div>
              <p className="text-base font-black text-foreground/70 uppercase tracking-tight">No questions added yet</p>
              <p className="text-xs text-muted-foreground mt-2 max-w-xs text-center leading-relaxed">Begin building your assessment by adding the first MCQ question.</p>
              <Button onClick={openCreate} className="mt-8 rounded-xl h-11 px-8 shadow-xl shadow-primary/20">
                 <Plus className="mr-2 h-4 w-4" /> Add Initial Question
              </Button>
           </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl flex flex-col p-0 overflow-hidden max-h-[95vh] border-none shadow-2xl rounded-2xl">
          <DialogHeader className="p-8 pb-4 bg-primary/5">
            <DialogTitle className="text-2xl font-bold">{editing ? "Refine Question" : "New MCQ Item"}</DialogTitle>
            <DialogDescription className="text-sm font-medium text-muted-foreground">Draft your question content and configure multiple-choice options.</DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-8 py-4 space-y-8">
            <form id="question-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                 <div className="space-y-3">
                    <Label className="text-sm font-semibold text-foreground">Question Text</Label>
                    <Textarea placeholder="Enter the MCQ question here..." className="resize-none shadow-sm h-28 font-medium leading-relaxed" {...form.register("text")} />
                    {form.formState.errors.text && <p className="text-xs font-bold text-destructive">{form.formState.errors.text.message}</p>}
                 </div>

                <div className="space-y-3">
                   <Label className="text-sm font-semibold text-foreground">Question Type</Label>
                   <select 
                     className="flex h-11 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm font-medium shadow-sm transition-all focus:border-primary focus:ring-1 focus:ring-primary/20"
                     value={form.watch("type") || "mcq"}
                     onChange={(e) => {
                       const type = e.target.value as "mcq" | "multiple" | "input";
                       form.setValue("type", type);
                       if (type === "mcq") {
                         form.setValue("correctAnswer", 0);
                       } else if (type === "multiple") {
                         form.setValue("correctAnswer", []);
                       } else {
                         form.setValue("correctAnswer", "");
                       }
                     }}
                   >
                     <option value="mcq">Single-Choice MCQ (Radio Buttons)</option>
                     <option value="multiple">Multi-Select (Checkboxes)</option>
                     <option value="input">Text Input Answer (Free Text)</option>
                   </select>
                </div>

                {form.watch("type") !== "input" ? (
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    <div className="lg:col-span-7 space-y-4">
                       <Label className="flex items-center gap-2 text-sm font-semibold text-foreground">
                          <LayoutList className="h-4 w-4 text-primary" /> Define Options
                       </Label>
                       <div className="space-y-3">
                          {[0, 1, 2, 3].map((i) => (
                             <div key={i} className="flex items-center gap-3">
                                <div className={cn(
                                   "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border font-black text-xs transition-all shadow-inner",
                                   form.watch("type") === "multiple" 
                                     ? (Array.isArray(form.watch("correctAnswer")) && form.watch("correctAnswer").includes(i) ? "bg-green-500 border-green-500 text-white" : "bg-muted text-muted-foreground")
                                     : (Number(form.watch("correctAnswer")) === i ? "bg-green-500 border-green-500 text-white shadow-green-500/20" : "bg-muted text-muted-foreground")
                                )}>
                                   {String.fromCharCode(65 + i)}
                                </div>
                                <Input 
                                   className="h-11 shadow-sm"
                                   placeholder={`Option ${String.fromCharCode(65 + i)} distractor...`} 
                                   {...form.register(`options.${i}` as any)} 
                                />
                             </div>
                          ))}
                       </div>
                    </div>

                    <div className="lg:col-span-5 space-y-6">
                       <div className="space-y-4 p-6 rounded-2xl border bg-muted/20 shadow-inner">
                          <Label className="text-[10px] font-black text-primary uppercase tracking-widest">
                            {form.watch("type") === "multiple" ? "Mark All Correct Answers" : "Mark Key Correct Answer"}
                          </Label>
                          {form.watch("type") === "multiple" ? (
                            <div className="grid grid-cols-2 gap-3">
                              {[0, 1, 2, 3].map(i => {
                                const val = form.watch("correctAnswer") || [];
                                const isChecked = Array.isArray(val) && val.includes(i);
                                
                                return (
                                  <div key={i} className={cn(
                                     "flex items-center gap-2 rounded-xl border p-3 cursor-pointer bg-background hover:bg-muted/50 transition-all",
                                     isChecked && "border-green-500 ring-1 ring-green-500/20 shadow-lg shadow-green-500/5 bg-green-50/50"
                                  )} onClick={() => {
                                    const current = Array.isArray(val) ? [...val] : [];
                                    if (current.includes(i)) {
                                      form.setValue("correctAnswer", current.filter(x => x !== i));
                                    } else {
                                      form.setValue("correctAnswer", [...current, i]);
                                    }
                                  }}>
                                     <Label className="cursor-pointer font-bold text-xs uppercase">Option {String.fromCharCode(65 + i)}</Label>
                                     {isChecked && <Check className="ml-auto h-3 w-3 text-green-500" />}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <RadioGroup 
                               value={String(form.watch("correctAnswer"))} 
                               onValueChange={(v) => form.setValue("correctAnswer", parseInt(v))}
                               className="grid grid-cols-2 gap-3"
                            >
                               {[0, 1, 2, 3].map(i => (
                                   <div key={i} className={cn(
                                      "flex items-center gap-2 rounded-xl border p-3 cursor-pointer bg-background hover:bg-muted/50 transition-all",
                                      Number(form.watch("correctAnswer")) === i && "border-green-500 ring-1 ring-green-500/20 shadow-lg shadow-green-500/5 bg-green-50/50"
                                   )} onClick={() => form.setValue("correctAnswer", i)}>
                                      <RadioGroupItem value={String(i)} id={`opt-${i}`} className="sr-only" />
                                      <Label htmlFor={`opt-${i}`} className="cursor-pointer font-bold text-xs uppercase">Option {String.fromCharCode(65 + i)}</Label>
                                      {Number(form.watch("correctAnswer")) === i && <Check className="ml-auto h-3 w-3 text-green-500" />}
                                   </div>
                               ))}
                            </RadioGroup>
                          )}
                       </div>

                        <div className="space-y-3">
                           <Label className="flex items-center gap-2 text-sm font-semibold text-foreground">
                             <Trophy className="h-4 w-4 text-yellow-500" /> Scoring Weight
                           </Label>
                          <Input type="number" className="h-11 shadow-sm font-mono text-base" {...form.register("marks")} />
                       </div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    <div className="lg:col-span-8 space-y-4">
                       <Label className="flex items-center gap-2 text-sm font-semibold text-foreground">
                          <Check className="h-4 w-4 text-green-500" /> Define Correct Text Answer
                       </Label>
                       <Input 
                          className="h-11 shadow-sm"
                          placeholder="Enter the exact correct text answer..." 
                          value={typeof form.watch("correctAnswer") === 'string' ? form.watch("correctAnswer") : ""}
                          onChange={(e) => form.setValue("correctAnswer", e.target.value)}
                       />
                       <p className="text-[10px] text-muted-foreground italic">Note: Text matches are case-insensitive and whitespace-trimmed automatically.</p>
                    </div>

                    <div className="lg:col-span-4 space-y-3">
                       <Label className="flex items-center gap-2 text-sm font-semibold text-foreground">
                         <Trophy className="h-4 w-4 text-yellow-500" /> Scoring Weight
                       </Label>
                       <Input type="number" className="h-11 shadow-sm font-mono text-base" {...form.register("marks")} />
                    </div>
                  </div>
                )}

                 <div className="space-y-3">
                    <Label className="text-sm font-semibold text-foreground">Answer Explanation / Rationale</Label>
                   <Textarea placeholder="Clarify why the correct option is the most accurate..." className="resize-none shadow-sm h-24 italic" {...form.register("explanation")} />
                </div>
             </form>
          </div>

          <DialogFooter className="p-8 border-t bg-muted/10">
            <Button variant="ghost" onClick={() => setOpen(false)} className="h-11 px-8 font-bold uppercase tracking-tight text-xs">Cancel</Button>
            <Button form="question-form" type="submit" disabled={addM.isLoading || updateM.isLoading} className="h-11 px-8 rounded-xl font-bold uppercase tracking-widest text-xs shadow-xl shadow-primary/20 group">
              {editing ? "Save Changes" : "Save Question"}
              <ChevronRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!toDelete}
        onOpenChange={(o) => !o && setToDelete(null)}
        onConfirm={async () => { if (toDelete) { await deleteQuestion(toDelete.id).unwrap(); toast.success("Question purged successfully"); setToDelete(null); } }}
        loading={deleteM.isLoading}
        title="Purge MCQ Item?"
        description="This will permanently remove the question from the assessment database."
      />
    </div>
  );
}
